import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, comparer, computed, IReactionDisposer, observable, reaction, toJS, makeObservable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { debounce, isNil, cloneDeep, omit, isEqual, set as _set, isEmpty } from 'lodash';
import { PrivilegeTypes } from '@bringg/types';
import type { SortingRule } from 'react-table';

import { APP_CONFIG, CONFIG_FIELDS_KEY_BY_TABLE_ID, CONFIG_TABLE_IDS, EMPTY_ROUTE_ID } from '../../consts';
import { hasAccess } from '../../utils/privileges';
import {
	getConfigWithFields,
	getTaskAlertsTableConfig,
	TableConfigCommon,
	TableConfigWithFields
} from './table-config-utils';
import { TaskAssignedStateEnum } from '../../types/common.consts';
import { workflowsRootStore } from 'bringg-web/features/automation-workflows/stores/internal';
import notification from 'bringg-web/services/notification';
import type RootStore from '../root-store';
import type { TableConfig, AdditionalFilters } from './table-config-utils';
import type { ExceptionFilters, TranslatableOption } from '../../types/common.consts';
import type { PresetViewAlertTableConfig } from 'bringg-web/features/preset-views/stores/stores';

interface ITableConfig {
	filtersObservable: ExceptionFilters;
	groupByObservable: string[];
	orderedColumnsObservable: TranslatableOption[];
	hiddenColumnsSet: Set<string>;
	sortByObservable: SortingRule<string>[];
	columnWidthsObservable: Record<string, number>;
	additionalFiltersNamesObservable: AdditionalFilters;
}

function sanitizeTableConfig(configOrigin: TableConfigWithFields, isImpersonatedMode: boolean): TableConfigWithFields {
	const config = Object.assign({}, configOrigin);

	if (isImpersonatedMode) {
		const fieldsToRemoveWhenImpersonated: (keyof TableConfigWithFields)[] = ['sortBy', 'filters', 'groupBy'];
		fieldsToRemoveWhenImpersonated.forEach(field => {
			delete config[field];
		});
	}
	if (config.fields && !config.fields.length) {
		delete config.fields;
	}

	return config;
}

function getSanitizedRouteIds(routeIds: number[], availableRouteIds: Set<number>) {
	return routeIds.filter(id => {
		if (id === EMPTY_ROUTE_ID) return true;

		return availableRouteIds.has(id);
	});
}

// TODO: fix lint error
// eslint-disable-next-line
export class TableConfigObject implements ITableConfig {
	private tableConfigReactionDisposer: IReactionDisposer = null;
	private dataLoadedReactionDisposer: IReactionDisposer = null;
	private routesReadyReactionDisposer: IReactionDisposer = null;
	protected defaultFilters: ExceptionFilters;
	protected readonly defaultOrderedColumns: TranslatableOption[] = [];

	isCustomizeListLayoutAllowed = false;
	readonly tableId: CONFIG_TABLE_IDS;
	readonly adminFieldConfig: string;

	filtersObservable: ExceptionFilters;
	groupByObservable: string[] = [];
	orderedColumnsObservable: TranslatableOption[] = []; // might require some default order
	hiddenColumnsSet: Set<string> = new Set();
	additionalFiltersNamesObservable: AdditionalFilters = [];

	// next are needed only to trigger reaction
	sortByObservable: SortingRule<string>[] = [];
	columnWidthsObservable: Record<string, number> = {};

	constructor(
		tableId: CONFIG_TABLE_IDS,
		defaultFilters: ExceptionFilters,
		defaultOrderedColumns: TranslatableOption[]
	) {
		makeObservable<
			TableConfigObject,
			| 'filters'
			| 'setFilters'
			| 'resetFilters'
			| 'groupBy'
			| 'setGroupBy'
			| 'columnOrder'
			| 'orderedColumns'
			| 'setOrderedColumns'
			| 'normalizedFilters'
			| 'sortBy'
			| 'setSortBy'
			| 'columnWidths'
			| 'setColumnWidths'
			| 'resetColumns'
			| 'hiddenColumnsSet'
			| 'hiddenColumns'
			| 'toggleHideColumn'
			| 'setNewFilters'
		>(this, {
			filtersObservable: observable.shallow,
			groupByObservable: observable.ref,
			orderedColumnsObservable: observable.ref,
			hiddenColumnsSet: observable.shallow,
			sortByObservable: observable.ref,
			columnWidthsObservable: observable.ref,
			additionalFiltersNamesObservable: observable.ref,
			normalizedFilters: computed,
			filters: computed,
			setNewFilters: action,
			setFilters: action,
			resetColumns: action,
			mergeConfig: action,
			hiddenColumns: computed,
			toggleHideColumn: action,
			columnOrder: computed,
			orderedColumns: computed,
			setOrderedColumns: action,
			groupBy: computed,
			setGroupBy: action,
			sortBy: computed,
			setSortBy: action,
			columnWidths: computed,
			setColumnWidths: action,
			resetFilters: action
		});

		this.tableId = tableId;
		this.adminFieldConfig = CONFIG_FIELDS_KEY_BY_TABLE_ID[tableId];
		this.defaultFilters = defaultFilters;
		this.filtersObservable = defaultFilters;
		this.orderedColumnsObservable = defaultOrderedColumns;
		this.defaultOrderedColumns = defaultOrderedColumns;
	}

	private debouncedUpdateTablesConfig = debounce((tableConfig: TableConfigCommon) => {
		const {
			usersStore: { isImpersonatedMode }
		} = getRoot<RootStore>().data;

		this.updateMerchantTablesConfig(
			sanitizeTableConfig(getConfigWithFields(tableConfig), isImpersonatedMode),
			this.tableId
		).catch(() => {
			console.warn('Unable to save table configuration data');
		});
	}, 2000);

	updateMerchantTablesConfig = async (configOrigin: TableConfigWithFields, tableId: CONFIG_TABLE_IDS) => {
		const { usersStore, merchantConfigurationsStore } = getRoot<RootStore>().data;
		const { isAdmin } = usersStore;
		const { sdk } = getRootEnv().dashboardSdk;
		const tableConfig = Object.assign({}, configOrigin);
		const promises: Promise<unknown>[] = [];

		// Note: we are using lodash.set directly but not this.config.set action
		// since no observable fields of config should be affected here
		const adminFieldsKey = CONFIG_FIELDS_KEY_BY_TABLE_ID[tableId];
		if (isAdmin && adminFieldsKey && Array.isArray(tableConfig.fields)) {
			// Protected by isEqual since the whole config.update request is heavy
			if (!isEqual(merchantConfigurationsStore.configuration[adminFieldsKey], tableConfig.fields)) {
				_set(merchantConfigurationsStore.configuration, adminFieldsKey, tableConfig.fields);
				promises.push(merchantConfigurationsStore.configuration.update());
			}
			delete tableConfig.fields;
		}

		if (!isEmpty(tableConfig)) {
			const merchantTablesConfiguration = merchantConfigurationsStore.getMerchantTablesConfiguration();
			const merchantExceptionTableConfig = merchantConfigurationsStore.getTableConfigurationByTableId(tableId);
			const newExceptionTableConfig = Object.assign(merchantExceptionTableConfig, tableConfig);

			// TODO: remove this snippet after Q2_2022
			// Remove old structure fields, from data stored in config
			['columnWidths', 'orderedColumns', 'hiddenColumnIds'].forEach(key => {
				delete newExceptionTableConfig[key];
			});

			_set(merchantTablesConfiguration, tableId, newExceptionTableConfig);
			promises.push(sdk.merchantConfiguration.dashboardTableConfigurationUpdate(merchantTablesConfiguration));
		}

		try {
			await Promise.all(promises);
		} catch (error) {
			// TODO: use translation here
			notification.error(String((error as any).message ?? error));
		}
	};

	disposeReactions = () => {
		this.tableConfigReactionDisposer?.();
		this.tableConfigReactionDisposer = undefined;

		this.dataLoadedReactionDisposer?.();
		this.dataLoadedReactionDisposer = undefined;

		this.routesReadyReactionDisposer?.();
		this.routesReadyReactionDisposer = undefined;
	};

	resetAfterRemoveRoot() {
		this.disposeReactions();
		this.debouncedUpdateTablesConfig.cancel();
	}

	flushUpdateTablesConfig = () => {
		this.debouncedUpdateTablesConfig.flush();
	};

	setupTableConfig = (presetViewTableConfig?: PresetViewAlertTableConfig) => {
		const {
			merchantConfigurationsStore: { getTableConfigurationByTableId, getAdminFields },
			usersStore: { currentUser, isImpersonatedMode }
		} = getRoot<RootStore>().data;
		const isPresetViewTable = Boolean(presetViewTableConfig);

		// hasAccess is covering isAdmin case
		this.isCustomizeListLayoutAllowed = hasAccess(currentUser, PrivilegeTypes.CUSTOMIZE_LIST_LAYOUT);

		const tableConfig = getTaskAlertsTableConfig(
			presetViewTableConfig ?? getTableConfigurationByTableId(this.tableId),
			getAdminFields(this.adminFieldConfig),
			isPresetViewTable || this.isCustomizeListLayoutAllowed,
			this.defaultOrderedColumns
		);

		if (tableConfig) {
			this.applyTableConfiguration(tableConfig);
		}

		if (APP_CONFIG.STORE_MERCHANT_TABLE_CONFIG && !isPresetViewTable) {
			this.tableConfigReactionDisposer = reaction(
				(): TableConfig => {
					const {
						groupBy,
						filters,
						sortBy,
						columnWidths,
						orderedColumns,
						hiddenColumns: hiddenColumnIds,
						additionalFiltersNames
					} = this;
					const reactionSubject = {};

					if (!isImpersonatedMode) {
						Object.assign(reactionSubject, { groupBy, filters, sortBy, additionalFiltersNames });
					}
					if (this.isCustomizeListLayoutAllowed) {
						Object.assign(reactionSubject, { columnWidths, orderedColumns, hiddenColumnIds });
					}

					return reactionSubject;
				},
				this.debouncedUpdateTablesConfig,
				{ equals: comparer.shallow }
			);
		}
	};

	applyTableConfiguration = (exceptionTableConfig: TableConfig) => {
		if (!Object.keys(exceptionTableConfig).length) return;

		const { filters, groupBy, sortBy, orderedColumns, hiddenColumnIds, columnWidths, additionalFiltersNames } =
			exceptionTableConfig;

		const { teamsStore, alertsStore } = getRoot<RootStore>().data;
		const { workflowAlertsStore } = workflowsRootStore.getStore();

		this.setFilters(this.defaultFilters);
		this.setGroupBy([]);
		this.setSortBy([]);
		this.setColumnWidths({});
		this.hiddenColumnsSet.clear();
		this.setAdditionalFiltersNames([]);

		if (filters && Object.keys(filters).length) {
			this.setFilters(filters);

			this.dataLoadedReactionDisposer = reaction(
				(): boolean => teamsStore.isFetched && !workflowAlertsStore.isLoading,
				isDataLoaded => {
					if (isDataLoaded) {
						this.mergeFilters(this.getSanitizedFilters(omit(filters, 'routes')));
						// Seems like we need this to be launched only once after data is loaded
						this.dataLoadedReactionDisposer();
					}
				}
			);

			// No need to apply anything if routes filter are safe by default = empty
			if (filters?.routes?.length) {
				// Routes can be applied safely only after all alerts + alerts related routes are fetched
				this.routesReadyReactionDisposer = reaction(
					(): boolean => alertsStore.isAllTaskAlertsDataLoaded,
					taskAlertsDataLoaded => {
						if (taskAlertsDataLoaded) {
							this.mergeFilters({
								routes: getSanitizedRouteIds(filters.routes, alertsStore.availableRouteIds)
							});
							this.routesReadyReactionDisposer();
						}
					}
				);
			}
		}

		if (groupBy?.length) {
			this.setGroupBy(groupBy);
		}
		if (sortBy?.length) {
			this.setSortBy(sortBy);
		}
		if (columnWidths && Object.values(columnWidths).length) {
			this.setColumnWidths(columnWidths);
		}
		if (hiddenColumnIds?.length) {
			hiddenColumnIds.forEach(id => this.toggleHideColumn(id));
		}

		if (orderedColumns?.length) {
			// Due to the fact that backend is shared by edge / prod front-end,
			// it is possible that columns config may have items which are not yet present on prod UI
			const activeColumnIds = new Set(this.defaultOrderedColumns.map(({ id }) => id));
			const uniqOrderedColumnsMap = orderedColumns.reduce((acc, item) => {
				acc.set(item.id, item);
				return acc;
			}, new Map());

			this.setOrderedColumns(
				Array.from(uniqOrderedColumnsMap.values()).filter(({ id }) => activeColumnIds.has(id))
			);
		}

		if (additionalFiltersNames?.length) {
			this.setAdditionalFiltersNames(additionalFiltersNames);
		}
	};

	protected get normalizedFilters(): Partial<ExceptionFilters> {
		const filters = toJS(this.filtersObservable);
		// this seems not very good, as teams/routes are missing for 2 of 3 tables
		const arrayKeys: ('teams' | 'routes' | 'alerts' | 'companies')[] = ['teams', 'routes', 'alerts', 'companies'];

		arrayKeys.forEach(key => {
			if (!filters[key]?.length) {
				delete filters[key];
			}
		});

		// assign status is missing for 2 of 3 tables
		if (filters.assignedState === TaskAssignedStateEnum.ALL) {
			delete filters.assignedState;
		}

		if (!filters.searchText?.trim()) {
			delete filters.searchText;
		}

		if (isNil(filters.alertOwner)) {
			delete filters.alertOwner;
		}

		if (isNil(filters.severity)) {
			delete filters.severity;
		}

		return filters;
	}

	protected get filters() {
		return toJS(this.filtersObservable);
	}

	mergeFilters = (partialFilters: Partial<ExceptionFilters>) => {
		Object.assign(this.filtersObservable, partialFilters);
	};

	protected setNewFilters = (partialFilters: Partial<ExceptionFilters>) => {
		// since the FilterBar2 from react-components returning
		// the form values on any  changes in form as partial of init values
		// we need to merge default filters(initial) with values of the form
		this.setFilters(Object.assign({}, this.defaultFilters, partialFilters));
	};

	protected resetFilters = (fieldName?: keyof ExceptionFilters) => {
		if (fieldName) {
			Object.assign(this.filtersObservable, { [fieldName]: this.defaultFilters[fieldName] });
		} else {
			this.setAdditionalFiltersNames([]);
			this.setFilters(this.defaultFilters);
		}
	};

	protected setFilters = (filters: ExceptionFilters) => {
		this.filtersObservable = filters;
	};

	protected resetColumns = () => {
		this.hiddenColumnsSet.clear();
		this.orderedColumnsObservable = this.defaultOrderedColumns;
	};

	mergeConfig = (partialConfig: Partial<ITableConfig>) => {
		Object.assign<ITableConfig, Partial<ITableConfig>>(this, partialConfig);
	};

	protected get hiddenColumns() {
		return Array.from(this.hiddenColumnsSet);
	}

	protected toggleHideColumn = (id: string) => {
		if (this.hiddenColumnsSet.has(id)) {
			this.hiddenColumnsSet.delete(id);
		} else {
			this.hiddenColumnsSet.add(id);
		}
	};

	protected get columnOrder() {
		return toJS(this.orderedColumnsObservable).map(({ id }) => id);
	}

	protected get orderedColumns() {
		return toJS(this.orderedColumnsObservable);
	}

	protected setOrderedColumns = (columns: TranslatableOption[]) => {
		this.orderedColumnsObservable = columns;
	};

	protected get groupBy() {
		return toJS(this.groupByObservable);
	}

	protected setGroupBy = (ids: string[]) => {
		this.groupByObservable = ids;
	};

	protected get sortBy() {
		return toJS(this.sortByObservable);
	}

	protected setSortBy = (sortBy: SortingRule<string>[]) => {
		this.sortByObservable = sortBy;
	};

	protected get columnWidths() {
		return toJS(this.columnWidthsObservable);
	}

	protected setColumnWidths = (columnWidths: Record<string, number>) => {
		this.columnWidthsObservable = columnWidths;
	};

	getSanitizedFilters = (filters: ExceptionFilters): Partial<ExceptionFilters> => {
		const newFilters = cloneDeep(filters);

		const { alertsStore, teamsStore } = getRoot<RootStore>().data;
		const alertTypesFilteredByConfig = alertsStore.alertTypesFilteredByConfig;
		const { workflowAlertsStore } = workflowsRootStore.getStore();

		// filter values should be aligned with existing options
		if (newFilters.alerts?.length) {
			let alertTypes = [];
			const customAlertTypes = workflowAlertsStore.alertTypes;

			switch (this.tableId) {
				case CONFIG_TABLE_IDS.DRIVER_ALERTS:
					alertTypes = alertTypesFilteredByConfig.driverAlerts;
					break;

				case CONFIG_TABLE_IDS.OPTIMIZATION_ALERTS:
					alertTypes = alertTypesFilteredByConfig.optimizationAlerts.concat(customAlertTypes);
					break;

				case CONFIG_TABLE_IDS.EXCEPTIONS:
					alertTypes = alertTypesFilteredByConfig.taskAlerts.concat(customAlertTypes);
					break;

				default:
					break;
			}

			// Could be that some valid values will be filtered out because they are not yet fetch
			newFilters.alerts = newFilters.alerts.filter(type => alertTypes.indexOf(type) !== -1);
		}

		if (newFilters.teams?.length) {
			// Could be that some valid values will be filtered out because they are not yet fetch
			newFilters.teams = newFilters.teams.filter(teamId => teamsStore.teams.has(teamId));
		}

		if (newFilters.routes?.length) {
			// Could be that some valid values will be filtered out because they are not yet fetch
			newFilters.routes = getSanitizedRouteIds(newFilters.routes, alertsStore.availableRouteIds);
		}

		return newFilters;
	};

	filterOutNotRelevantRoutesFromFilter() {
		if (!this.filters.routes?.length) {
			return;
		}

		const { alertsStore } = getRoot<RootStore>().data;
		const newFilters = { ...this.filters };
		const filteredRoutes = getSanitizedRouteIds(newFilters.routes, alertsStore.availableRouteIds);

		if (newFilters.routes.length !== filteredRoutes.length) {
			newFilters.routes = filteredRoutes;
			this.setFilters(newFilters);
		}
	}

	setAdditionalFiltersNames = (filtersKey: AdditionalFilters) => {
		this.additionalFiltersNamesObservable = filtersKey;
	};

	get additionalFiltersNames() {
		return toJS(this.additionalFiltersNamesObservable);
	}
}
